import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { Trans, useTranslation } from 'react-i18next'

import { Box, Text } from '@chakra-ui/react'

import type { ColorScheme, SegmentedControlOption } from '@hivewatch/dark'
import { SegmentedControl } from '@hivewatch/dark'

import { IncidentsView } from '@/redux/incidents/incidentsSlice'
import { BUTTON_PRESS, trackEvent } from '@/utils/analytics'

interface IProps {
  colorScheme: ColorScheme
  view: IncidentsView
  onSelectView: (view: IncidentsView) => void
}

// Bold/emphasis text in the dark package uses Soehne Kraftig at weight 500,
// not fontWeight='bold' - see packages/dark/CLAUDE.md's Style Guide.
const emphasisStyles = {
  fontFamily: "'Soehne Kraftig', sans-serif",
  fontWeight: '500',
} as const

/**
 * Priority Queue, All, and Acknowledged by Me as mutually exclusive options, in
 * that left-to-right order (ENG-32285, dark-rebrand parity for ENG-32284).
 * Supersedes the standalone `DarkPriorityQueueToggle`, which rendered Priority
 * Queue as an extra segment stacked onto this control via `alwaysClickable`.
 */
export const DarkIncidentsOwnerToggle = ({
  colorScheme,
  view,
  onSelectView,
}: IProps) => {
  const { t } = useTranslation()
  const isPriorityQueueEnabled = useFeatureFlag(FeatureFlag.priorityQueue)

  const priorityQueueOption: SegmentedControlOption = {
    value: 'priorityQueue',
    label: t('incidents:priority_queue_toggle'),
    tooltip: (
      <Box>
        <Text {...emphasisStyles}>{t('incidents:priority_queue_toggle')}</Text>
        <Text>
          <Trans
            components={{ b: <Text as='span' {...emphasisStyles} /> }}
            i18nKey={'incidents:priority_queue_tooltip_description' as never}
          />
        </Text>
      </Box>
    ),
    tooltipVariant: 'description',
  }

  const onChange = (value: string) => {
    const nextView = value as IncidentsView
    if (nextView === 'all') {
      trackEvent(`${BUTTON_PRESS} Filter All Incidents`)
    } else if (nextView === 'acknowledgedByMe') {
      trackEvent(`${BUTTON_PRESS} Filter Incidents Acknowledged by Me`)
    }
    onSelectView(nextView)
  }

  return (
    <SegmentedControl
      colorScheme={colorScheme}
      height='36px'
      onChange={onChange}
      options={[
        ...(isPriorityQueueEnabled ? [priorityQueueOption] : []),
        { value: 'all', label: t('incidents:filter_owner_all') },
        { value: 'acknowledgedByMe', label: t('incidents:filter_owner_me') },
      ]}
      value={view}
    />
  )
}
